import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router'
import { RawStatusBadge, StatusBadge, TypeTag } from '../components/Badge'
import { FreshnessBanner } from '../components/FreshnessBanner'
import { LineCard } from '../components/LineCard'
import { EmptyState, ErrorState, LoadingState } from '../components/states'
import {
  fetchOrderDetail,
  type OrderDetailHeader,
  type OrderInvoice,
  type OrderLine,
} from '../data/orders'
import { formatAmount, formatDate, formatIdentifier } from '../lib/format'
import './OrderDetailScreen.css'

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; detail: string }
  | { status: 'not_found' }
  | { status: 'ready'; order: OrderDetailHeader; lines: OrderLine[]; invoices: OrderInvoice[] }

/**
 * Route `/orders/:id`.
 *
 * A co-credited order reads its header and lines here exactly like a
 * single-rep order: orders_rep_read / order_lines_rep_read return what the
 * caller is credited on, and the total is the view's sum of those lines.
 * "Shared" is orders_display.is_shared, which calls the existing
 * public.order_has_co_credit wrapper — a boolean and nothing else: no rep_id,
 * no commission figure, no share or ratio.
 */
export function OrderDetailScreen() {
  const { id } = useParams<{ id: string }>()
  const [state, setState] = useState<LoadState>({ status: 'loading' })

  const load = useCallback(() => {
    if (!id) return
    setState({ status: 'loading' })
    void fetchOrderDetail(id).then((result) => {
      if (result.status === 'error') {
        setState({ status: 'error', detail: result.detail })
        return
      }
      if (result.status === 'not_found') {
        setState({ status: 'not_found' })
        return
      }
      setState({ status: 'ready', order: result.order, lines: result.lines, invoices: result.invoices })
    })
  }, [id])

  useEffect(() => {
    load()
  }, [load])

  if (!id) {
    return <EmptyState title="No order specified" />
  }

  if (state.status === 'loading') {
    return <LoadingState label="Loading order…" />
  }

  if (state.status === 'error') {
    return <ErrorState title="We could not load this order" detail={state.detail} onRetry={load} />
  }

  if (state.status === 'not_found') {
    return <EmptyState title="Order not found" detail={`No order matches ${id}.`} />
  }

  const { order, lines, invoices } = state
  const shipTo = [order.shipToName, [order.shipToCity, order.shipToState].filter(Boolean).join(', ')]
    .filter(Boolean)
    .join(' · ')

  return (
    <article className="esl-order">
      <FreshnessBanner />
      <p className="esl-identifier">{formatIdentifier(order.orderNo)}</p>
      <h1>{order.customerName ?? 'Unknown Customer'}</h1>

      <div className="esl-order__meta">
        <RawStatusBadge status={order.status} />
        {order.hasReturn ? <TypeTag icon="return" label="Return" /> : null}
        {order.isShared ? <TypeTag icon="shared" label="Shared" /> : null}
        {order.hasPastPromise ? <StatusBadge role="danger" icon="alert" label="Past ESL promise" /> : null}
      </div>

      <dl className="esl-order__facts">
        {shipTo ? (
          <div>
            <dt>Ship to</dt>
            <dd>{shipTo}</dd>
          </div>
        ) : null}
        {order.custPo ? (
          <div>
            <dt>PO</dt>
            <dd className="esl-order__mono">{formatIdentifier(order.custPo)}</dd>
          </div>
        ) : null}
        {order.jobName ? (
          <div>
            <dt>Job</dt>
            <dd>{order.jobName}</dd>
          </div>
        ) : null}
        <div>
          <dt>Order date</dt>
          <dd>{formatDate(order.orderDate)}</dd>
        </div>
        {order.requestedDate ? (
          <div>
            <dt>Requested</dt>
            <dd>{formatDate(order.requestedDate)}</dd>
          </div>
        ) : null}
      </dl>

      {/* White card, brand accent rule: no green fill, brand green never as
          text (status colours option C). */}
      <div className="esl-hero esl-order__total">
        <p className="esl-hero-label">Total</p>
        <p className="esl-hero-value">{formatAmount(order.orderTotal)}</p>
      </div>

      {/* Boolean-driven, nothing else — unchanged wording. */}
      {order.isShared ? (
        <p className="esl-order__co-credit-banner">Shared with another ESL agency.</p>
      ) : null}

      <section className="esl-order__lines">
        <h2>Lines</h2>

        {lines.length === 0 ? (
          <EmptyState title="No lines" detail="This order has no line items on record." />
        ) : (
          <ul className="esl-line-list">
            {lines.map((line) => (
              <LineCard
                key={line.lineNo}
                line={line}
                heading={
                  line.itemId ? (
                    <Link to={`/sku/${line.itemId}`} className="esl-line-card__link">
                      {formatIdentifier(line.itemId)}
                    </Link>
                  ) : (
                    <span className="esl-muted">No item on record</span>
                  )
                }
              />
            ))}
          </ul>
        )}
      </section>

      <section className="esl-order__invoices">
        <h2>Invoices</h2>
        {/* Since 0036 (Jeff's ruling, 2026-11-02) an agency credited on an
            order reads every invoice on it, including ones P21 credits only
            to another agency (e.g. 1073655, 1083024). So an empty list means
            no invoice with this order number exists yet. Nothing shipped on
            any line: "Not invoiced yet." Something shipped but no invoice on
            record (1 of 19,483 shipped orders on 2026-11-03): say so, without
            implying it is someone else's. */}
        {invoices.length === 0 ? (
          <EmptyState
            title={
              lines.some((l) => (l.qtyShipped ?? 0) !== 0)
                ? 'Shipped. No invoice on record yet.'
                : 'Not invoiced yet.'
            }
          />
        ) : (
          <>
          {/* Verified 2026-11-02: invoice lines sum exactly to invoice_total on
              every invoice, and invoices carry lines the order does not
              (Outgoing Freight on 903 lines, shipping adjustments, warranty
              discounts, incentives). No tax lines exist in the data, so the
              note does not claim tax. */}
          <p className="esl-muted esl-order__invoice-note">
            Invoice totals can include freight and other charges.
          </p>
          <ul className="esl-line-list">
            {invoices.map((inv) => (
              <li className="esl-line-card" key={inv.invoiceNo}>
                <div className="esl-line-card__head">
                  <span className="esl-identifier">INVOICE {formatIdentifier(inv.invoiceNo)}</span>
                  <span className="esl-line-card__value">{formatAmount(inv.invoiceTotal)}</span>
                </div>
                <div className="esl-line-card__row">
                  <span>Invoiced {formatDate(inv.invoiceDate)}</span>
                  <span>Shipped {formatDate(inv.shipDate)}</span>
                </div>
                {inv.carrier ? (
                  <p className="esl-muted esl-order__carrier">Carrier: {inv.carrier}</p>
                ) : null}
              </li>
            ))}
          </ul>
          </>
        )}
      </section>
    </article>
  )
}

